import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import inkfreeLogo from "assets/images/signer_inkfree_logo.svg";
import CustomAuthCardLayout from "global/components/CustomAuthCardLayout/CustomAuthCardLayout";
import SignerCard from "screens/SignRequest/ParticipantScreen/components/SignerCard";
import { useSearchParams } from "hooks/use-search-params";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import strings from "global/constants/StringConstants";
import {
  CARD,
  CODE_ROW,
  CONTACT_ROW,
  EXPIRED_VISUAL,
  NOTFOUND_VISUAL,
} from "./LinkExpired.tokens";

/**
 * "Request a new link" needs `POST /api/sign/link/{code}/request-new`, which
 * does not exist yet (see Backend Requests in the PR/handoff). Flip this once
 * that endpoint is agreed and wired up — until then the CTA stays hidden
 * rather than pointing at an endpoint that doesn't exist.
 */
const REQUEST_NEW_LINK_ENABLED = false;

const ClockGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    width={30}
    height={30}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

const SearchGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    width={30}
    height={30}
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const LinkExpiredComponent = () => {
  useTitle(title.LINK_EXPIRED);

  const searchParams = useSearchParams();
  const code = searchParams.get("c") || "";
  const epochMillis = Number(searchParams.get("e"));
  const isExpired =
    searchParams.get("r") === "expired" && Number.isFinite(epochMillis) && epochMillis > 0;

  const visual = isExpired ? EXPIRED_VISUAL : NOTFOUND_VISUAL;

  const heading = isExpired
    ? `This signing link expired on ${moment(epochMillis).format("D MMMM YYYY")}.`
    : "We can't find this signing link.";

  const body = isExpired
    ? "Signing links stay valid for 7 days so that documents can't be signed from an old message."
    : "It may have been typed incorrectly, or it may be more than a month old.";

  // Whom to contact. The page holds no envelope — a dead link resolves to
  // nothing — so the sender cannot be named here; the copy points at "whoever
  // sent you this document" instead, which is always true and always the only
  // person who can issue a replacement.
  const contact = isExpired
    ? strings.linkExpired.contactExpired
    : strings.linkExpired.contactNotFound;

  return (
    <Box>
      {/* CustomAuthCardLayout -> AuthLayout already renders the single
       * `<main id="main-content">` landmark for this direct (non-Layout)
       * route — do not add a second one here (§11.8: one main per page). */}
      <CustomAuthCardLayout minWidth="30%" IsCustomAuthCardLayout noCard signerTheme>
        <SignerCard maxWidth={CARD.width} strip={visual.strip} sx={{ boxShadow: CARD.shadow }}>
          <Box
            sx={{
              px: { xs: 3.5, sm: `${CARD.bodyPadX}px` },
              pt: `${CARD.bodyPadTop}px`,
              pb: "40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src={inkfreeLogo}
              alt="Inkfree"
              sx={{ width: CARD.logoWidth, height: "auto" }}
            />

            <Box
              aria-hidden
              sx={{
                width: CARD.circleSize,
                height: CARD.circleSize,
                borderRadius: "50%",
                background: visual.circle,
                boxShadow: visual.circleShadow,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: `${CARD.circleGap}px`,
              }}
            >
              {isExpired ? <ClockGlyph /> : <SearchGlyph />}
            </Box>

            <Typography
              component="h1"
              sx={{
                mt: `${CARD.titleGap}px`,
                fontSize: CARD.titleSize,
                fontWeight: 800,
                color: CARD.titleColor,
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
              }}
            >
              {heading}
            </Typography>

            <Typography
              sx={{
                mt: `${CARD.bodyGap}px`,
                fontSize: CARD.bodySize,
                lineHeight: CARD.bodyLineHeight,
                color: CARD.bodyColor,
                maxWidth: CARD.bodyMaxWidth,
              }}
            >
              {body}
            </Typography>

            <Box
              sx={{
                mt: `${CONTACT_ROW.gap}px`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: CONTACT_ROW.dividerWidth,
                  height: "1px",
                  bgcolor: CONTACT_ROW.divider,
                  mb: `${CONTACT_ROW.dividerGap}px`,
                }}
              />
              <Typography
                sx={{
                  fontSize: CONTACT_ROW.fontSize,
                  lineHeight: CONTACT_ROW.lineHeight,
                  color: CONTACT_ROW.color,
                  maxWidth: CONTACT_ROW.maxWidth,
                }}
              >
                {contact}
              </Typography>
              <Typography
                sx={{
                  mt: `${CONTACT_ROW.noteGap}px`,
                  fontSize: CONTACT_ROW.noteFontSize,
                  lineHeight: CONTACT_ROW.lineHeight,
                  color: CONTACT_ROW.noteColor,
                  maxWidth: CONTACT_ROW.maxWidth,
                }}
              >
                {strings.linkExpired.contactNote}
              </Typography>
            </Box>

            {REQUEST_NEW_LINK_ENABLED && (
              <Button variant="contained" sx={{ mt: 3.25, height: 42, px: 3.5 }}>
                Request a new link
              </Button>
            )}

            {code && (
              <Typography sx={{ mt: `${CODE_ROW.gap}px`, fontSize: CODE_ROW.fontSize, color: CODE_ROW.color }}>
                Code: {code}
              </Typography>
            )}
          </Box>
        </SignerCard>
      </CustomAuthCardLayout>
    </Box>
  );
};

export default LinkExpiredComponent;
